import './LivePrompt.sass';

import React, { ChangeEventHandler, FC, useCallback, useEffect } from 'react';
import { IcDollar28, IcSend28 } from '$assets';
import { LivePromptProps } from './LivePrompt.interface';
import { classNames as cn, statReachGoal } from '$utils';
import { useDispatch, useSelector } from 'react-redux';
import { liveActions, liveSelector } from '$store/live';
import { useTranslator } from '$hooks';
import { balanceActions } from '$store/balance';
import { CurrencyEnum } from '$store/models';

export const LivePrompt: FC<LivePromptProps> = ({ disabled, tips }) => {
  const dispatch = useDispatch();
  const { message, stream } = useSelector(liveSelector);
  const textareaRef = React.createRef<HTMLTextAreaElement>();
  const t = useTranslator();

  const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((e) => {
    dispatch(liveActions.setMessage(e.target.value));
  }, []);

  const handleSend = useCallback(() => {
    dispatch(liveActions.sendMessage());
    textareaRef.current?.focus();
    statReachGoal('live_send_msg');
  }, [textareaRef, message]);

  const handleSendTips = useCallback(() => {
    stream?.streamerId &&
      dispatch(
        balanceActions.setTipsData({
          peerId: stream.streamerId,
          currency: CurrencyEnum.EUR,
          liveId: stream.id,
        }),
      );
  }, [stream]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [message]);

  return (
    <div className={cn('LivePrompt', { disabled })}>
      {tips && (
        <div className="LivePrompt__action" onClick={handleSendTips}>
          <IcDollar28 />
        </div>
      )}
      <div className="LivePrompt__input">
        <textarea
          ref={textareaRef}
          onFocus={() => dispatch(liveActions.setFocus(true))}
          onBlur={() => dispatch(liveActions.setFocus(false))}
          onChange={handleChange}
          rows={1}
          value={message}
          placeholder={t('live_prompt_placeholder')}
        />
      </div>
      <div onClick={handleSend} className="LivePrompt__send">
        <IcSend28 />
      </div>
    </div>
  );
};
